import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  completeAppleOAuth,
  completeGoogleOAuth,
  adminExitImpersonation,
  confirmPasswordReset,
  fetchMe,
  fetchProfessionProfile,
  login as apiLogin,
  logout as apiLogout,
  primeCoreCaches,
  register as apiRegister,
  restoreAuthSession,
  setAuthToken,
} from "../api";
import {
  clearAdminWorkspaceSessionMeta,
  clearAdminWorkspacePreviousSession,
  readAdminWorkspaceSessionMeta,
  readAdminWorkspacePreviousSession,
} from "../adminWorkspaceSession";
import { buildSignInUrl } from "../utils/authLinks";
import { syncAccountLocale } from "../i18n/accountLocale.js";
import { storedSessionChangedAccount } from "../utils/sessionAccount";
import { teamAccountCacheKey } from "../utils/teamAccess";

let memoryToken = null;
let memoryUser = null;

// Pages of the business app's host that need the business session. Only these
// try to recover it from the API refresh cookie: the marketing pages on the
// same host are mostly visited signed out, and each try there was a POST
// /auth/refresh answered 401 (and a console error) on every page view.
// The app moved back from business.offlico.com to www.offlico.com on 25 Oct
// 2026 (business.offlico.com now redirects to www). /team is the Team plan's
// invite page (/team/join/<token>), which needs to know who is signed in.
const BUSINESS_SESSION_PATHS = ["/app", "/upgrade", "/tasks", "/shared-calendar", "/signin", "/team"];
const BUSINESS_SESSION_HOSTS = ["www.offlico.com", "offlico.com", "business.offlico.com"];

export function shouldRestoreBusinessSession(hostname, pathname) {
  if (!BUSINESS_SESSION_HOSTS.includes(String(hostname || "").toLowerCase())) return false;
  const path = String(pathname || "/");
  return BUSINESS_SESSION_PATHS.some((base) => path === base || path.startsWith(`${base}/`));
}

function readStoredToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      return window.localStorage.getItem("scheduler.token");
    } catch {
      return null;
    }
  }
  return memoryToken;
}

function readStoredUser() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem("scheduler.user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
  return memoryUser;
}

function writeStoredToken(value) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem("scheduler.token", value);
      return;
    } catch {
      // ignore
    }
  }
  memoryToken = value;
}

function writeStoredUser(value) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem("scheduler.user", JSON.stringify(value));
      return;
    } catch {
      // ignore
    }
  }
  memoryUser = value;
}

function clearStoredToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem("scheduler.token");
      return;
    } catch {
      // ignore
    }
  }
  memoryToken = null;
}

function clearStoredUser() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem("scheduler.user");
      return;
    } catch {
      // ignore
    }
  }
  memoryUser = null;
}

const PROFESSION_CACHE_TTL_MS = 5 * 60 * 1000;

function professionCacheKey(userId) {
  return userId ? `scheduler.profession.${userId}` : null;
}

function readCachedProfession(userId) {
  if (typeof window === "undefined" || !window.localStorage || !userId) return null;
  try {
    const raw = window.localStorage.getItem(professionCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const timestamp = Number(parsed.timestamp || 0);
    if (!Number.isFinite(timestamp) || Date.now() - timestamp > PROFESSION_CACHE_TTL_MS) return null;
    return {
      professionType: parsed.professionType || null,
      professionProfile: parsed.professionProfile || null,
    };
  } catch {
    return null;
  }
}

function writeCachedProfession(userId, professionType, professionProfile) {
  if (typeof window === "undefined" || !window.localStorage || !userId) return;
  try {
    window.localStorage.setItem(
      professionCacheKey(userId),
      JSON.stringify({
        timestamp: Date.now(),
        professionType: professionType || null,
        professionProfile: professionProfile || null,
      }),
    );
  } catch {
    // ignore storage failures
  }
}

const AuthContext = createContext({
  status: "loading",
  user: null,
  actor: null,
  impersonation: null,
  token: null,
  professionType: null,
  professionProfile: null,
  professionStatus: "idle",
  signIn: async () => {},
  register: async () => {},
  signOut: () => {},
  exitImpersonation: async () => {},
  refreshProfessionProfile: async () => {},
  refreshUser: async () => {},
  updateUserLocale: () => {},
  signInWithApple: async () => {},
  signInWithGoogle: async () => {},
  completePasswordReset: async () => {},
  applySignedInSession: async () => {},
  applyAuthState: async () => {},
});

export function AuthProvider({ children }) {
  const location = useLocation();
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUserState] = useState(() => {
    const stored = readStoredUser();
    // Before the first render, so the app's money, dates and distances show in
    // the account's region from the start (user.locale; none = a UK account).
    syncAccountLocale(stored);
    return stored;
  });
  const setUser = useCallback((nextUser) => {
    syncAccountLocale(nextUser);
    setUserState(nextUser);
  }, []);
  const [actor, setActor] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [status, setStatus] = useState("loading");
  const [professionType, setProfessionType] = useState(null);
  const [professionProfile, setProfessionProfile] = useState(null);
  const [professionStatus, setProfessionStatus] = useState("idle");
  const professionReqSeqRef = useRef(0);
  const userRef = useRef(user);
  // Set by signOut: the refresh cookie is being revoked, so the session must not
  // be recovered from it (a recovery racing the logout signed the user back in).
  const signedOutRef = useRef(false);
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    userRef.current = user;
    syncAccountLocale(user);
  }, [user]);

  const resetProfessionState = () => {
    setProfessionType(null);
    setProfessionProfile(null);
    setProfessionStatus("idle");
  };

  const clearSession = useCallback(() => {
    professionReqSeqRef.current += 1;
    clearAdminWorkspaceSessionMeta();
    clearStoredToken();
    clearStoredUser();
    setAuthToken(null);
    setUser(null);
    setActor(null);
    setImpersonation(null);
    setToken(null);
    setStatus("ready");
    resetProfessionState();
  }, [setUser]);

  const hydrateProfessionProfile = useCallback(async () => {
    if (!token) {
      resetProfessionState();
      return null;
    }
    const requestSeq = ++professionReqSeqRef.current;
    const activeUser = userRef.current;
    const activeUserId = teamAccountCacheKey(activeUser) || null;
    const cached = readCachedProfession(activeUserId);
    if (cached) {
      setProfessionType(cached.professionType || activeUser?.profession_type || null);
      setProfessionProfile(cached.professionProfile || null);
      setProfessionStatus("ready");
    } else {
      setProfessionStatus("loading");
    }
    try {
      const payload = await fetchProfessionProfile({ forceRefresh: true });
      if (requestSeq !== professionReqSeqRef.current) return null;
      const nextType = payload?.profession_type || activeUser?.profession_type || null;
      const nextProfile = payload?.profile || null;
      setProfessionType(nextType);
      setProfessionProfile(nextProfile);
      setProfessionStatus("ready");
      if (activeUserId) {
        writeCachedProfession(activeUserId, nextType, nextProfile);
      }
      return payload;
    } catch (err) {
      console.error("Unable to fetch profession profile:", err);
      if (requestSeq !== professionReqSeqRef.current) return null;
      if (!cached) {
        setProfessionStatus("error");
      }
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      // The business workspace can recover its API refresh session after moving
      // from www. Customer Hub has its own customer token, not this auth state.
      if (
        typeof window !== "undefined" &&
        !signedOutRef.current &&
        shouldRestoreBusinessSession(window.location.hostname, window.location.pathname)
      ) {
        let cancelled = false;
        setStatus("verifying");
        restoreAuthSession()
          .then((session) => {
            if (cancelled) return;
            if (!session?.access_token) {
              clearSession();
              return;
            }
            writeStoredToken(session.access_token);
            if (session.user) {
              writeStoredUser(session.user);
              setUser(session.user);
            }
            setToken(session.access_token);
          })
          .catch(() => {
            if (!cancelled) clearSession();
          });
        return () => {
          cancelled = true;
        };
      }
      clearSession();
      return;
    }

    let cancelled = false;
    const verificationToken = token;
    const timeoutMs = 6000;
    const hadCachedUser = Boolean(user);
    if (hadCachedUser) {
      setStatus("ready");
      const cachedProfession = readCachedProfession(teamAccountCacheKey(user));
      if (cachedProfession) {
        setProfessionType(cachedProfession.professionType || user?.profession_type || null);
        setProfessionProfile(cachedProfession.professionProfile || null);
        setProfessionStatus("ready");
      } else {
        setProfessionType(user?.profession_type || null);
      }
    } else {
      setStatus("verifying");
    }
    setAuthToken(token);
    const timeoutPromise = new Promise((_, reject) => {
      const id = setTimeout(() => {
        clearTimeout(id);
        reject(new Error("auth timeout"));
      }, timeoutMs);
    });
    Promise.race([fetchMe(), timeoutPromise])
      .then((session) => {
        if (cancelled || verificationToken !== token) return;
        setUser(session.user);
        writeStoredUser(session.user);
        setActor(session.actor);
        setImpersonation(session.impersonation || null);
        const cachedProfession = readCachedProfession(teamAccountCacheKey(session.user));
        if (cachedProfession) {
          setProfessionType(cachedProfession.professionType || session.user?.profession_type || null);
          setProfessionProfile(cachedProfession.professionProfile || null);
          setProfessionStatus("ready");
        } else {
          setProfessionType(session.user?.profession_type || null);
        }
        setStatus("ready");
        primeCoreCaches();
        hydrateProfessionProfile();
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.status === 401 || !hadCachedUser) {
          clearSession();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clearSession, hydrateProfessionProfile, token]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleStorage = (event) => {
      if (event.key !== null && event.key !== "scheduler.token") return;
      if (storedSessionChangedAccount(token, readStoredToken())) {
        // Start again as the account the browser is now signed in as, so its
        // plan decides what this tab can open (pricing for an unpaid account).
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [token]);

  useEffect(() => {
    const handleInvalid = () => {
      clearSession();
    };
    if (typeof window !== "undefined") {
      window.addEventListener("auth:invalid", handleInvalid);
      return () => window.removeEventListener("auth:invalid", handleInvalid);
    }
    return undefined;
  }, [clearSession]);

  useEffect(() => {
    if (!isAdminRoute) return;
    const previous = readAdminWorkspacePreviousSession();
    if (!previous) return;
    const nextToken =
      previous.token === "null" || previous.token === "undefined" || !previous.token ? null : previous.token;
    const nextUser = previous.user ?? null;
    const nextUserSerialized = JSON.stringify(nextUser || null);
    const currentUserSerialized = JSON.stringify(user || null);
    const shouldRestoreUser = currentUserSerialized !== nextUserSerialized;

    if (nextToken === null && token === null && !shouldRestoreUser) {
      clearAdminWorkspacePreviousSession();
      return;
    }

    if (token !== nextToken || shouldRestoreUser) {
      if (nextToken === null) {
        clearStoredToken();
        setAuthToken(null);
        setToken(null);
      } else {
        writeStoredToken(nextToken);
        setAuthToken(nextToken);
        setToken(nextToken);
      }
      if (nextUser) {
        setUser(nextUser);
        writeStoredUser(nextUser);
      } else if (user !== null) {
        setUser(null);
        clearStoredUser();
      }
    }

    clearAdminWorkspacePreviousSession();
  }, [isAdminRoute, location.pathname, token, user]);

  useEffect(() => {
    const handleRefreshed = (event) => {
      const nextToken = event?.detail?.token;
      const nextUser = event?.detail?.user;
      if (!nextToken) return;
      setAuthToken(nextToken);
      setToken(nextToken);
      if (nextUser) {
        setUser(nextUser);
        writeStoredUser(nextUser);
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("auth:refreshed", handleRefreshed);
      return () => window.removeEventListener("auth:refreshed", handleRefreshed);
    }
    return undefined;
  }, []);

  const applySession = useCallback(
    async (res) => {
      signedOutRef.current = false;
      clearAdminWorkspaceSessionMeta();
      setAuthToken(res.access_token);
      writeStoredToken(res.access_token);
      setToken(res.access_token);
      userRef.current = res.user;
      setUser(res.user);
      writeStoredUser(res.user);
      setActor(res.user);
      setImpersonation(res.impersonation || null);
      setStatus("ready");
      setProfessionType(res.user?.profession_type || null);
      primeCoreCaches(true);
      await hydrateProfessionProfile();
      return res.user;
    },
    [hydrateProfessionProfile, setUser]
  );

  // The Team plan's accept and workspace switch answer with an AuthState
  // ({user, actor}) for the same token: the account now works in another
  // business, so its user, caches and profession are replaced.
  const applyAuthState = useCallback(
    async (state) => {
      if (!state?.user) return null;
      signedOutRef.current = false;
      userRef.current = state.user;
      setUser(state.user);
      writeStoredUser(state.user);
      setActor(state.actor || state.user);
      setImpersonation(state.impersonation || null);
      setProfessionType(state.user?.profession_type || null);
      setStatus("ready");
      primeCoreCaches(true);
      await hydrateProfessionProfile();
      return state.user;
    },
    [hydrateProfessionProfile]
  );

  const value = useMemo(
    () => ({
      status,
      user,
      token,
      async signIn(credentials) {
        return applySession(await apiLogin(credentials));
      },
      async register(data) {
        return applySession(await apiRegister(data));
      },
      async signInWithGoogle(input) {
        const payload = typeof input === "string" ? { code: input } : input;
        return applySession(await completeGoogleOAuth(payload));
      },
      async signInWithApple(payload) {
        return applySession(await completeAppleOAuth(payload));
      },
      async completePasswordReset(payload) {
        return applySession(await confirmPasswordReset(payload));
      },
      // A TokenResponse from elsewhere (the Team invite's "Create your login").
      applySignedInSession: applySession,
      applyAuthState,
      signOut() {
        signedOutRef.current = true;
        apiLogout().catch(() => {});
        clearSession();
      },
      async exitImpersonation() {
        const workspaceMeta = readAdminWorkspaceSessionMeta();
        if (workspaceMeta) {
          const returnTo = workspaceMeta.returnTo || "/admin/users/directory";
          clearSession();
          if (typeof window !== "undefined") {
            window.location.assign(returnTo);
          }
          return;
        }
        const res = await adminExitImpersonation();
        setAuthToken(res.access_token);
        writeStoredToken(res.access_token);
        setToken(res.access_token);
        setUser(res.user);
        writeStoredUser(res.user);
        setActor(res.user);
        setImpersonation(null);
        setStatus("ready");
        setProfessionType(res.user?.profession_type || null);
        primeCoreCaches(true);
        await hydrateProfessionProfile();
      },
      actor,
      impersonation,
      professionType,
      professionProfile,
      professionStatus,
      refreshProfessionProfile: hydrateProfessionProfile,
      // Region & language settings saved: the new locale applies at once.
      updateUserLocale(locale) {
        const current = userRef.current;
        if (!current || !locale || typeof locale !== "object") return;
        const nextUser = { ...current, locale };
        userRef.current = nextUser;
        setUser(nextUser);
        writeStoredUser(nextUser);
      },
      refreshUser: async () => {
        if (!token) return null;
        const session = await fetchMe();
        setUser(session.user);
        writeStoredUser(session.user);
        setActor(session.actor);
        setImpersonation(session.impersonation || null);
        setProfessionType(session.user?.profession_type || null);
        return session.user;
      },
    }),
    [actor, applyAuthState, applySession, clearSession, impersonation, professionProfile, professionStatus, professionType, setUser, status, token, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === "loading" || status === "verifying") {
    return <div className="authGate">Checking credentials…</div>;
  }
  if (!user) {
    const isLocalHost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "::1");
    const query = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const isTasksPreview = isLocalHost && location.pathname === "/app" && query?.get("section") === "tasks";
    const skipAuth = query?.get("dev") === "1" || query?.get("skipAuth") === "1";
    if (isTasksPreview || (isLocalHost && skipAuth && !import.meta.env.PROD)) {
      return children;
    }
    const needsUpgradeRedirect = location.pathname === "/upgrade";
    const upgradeRedirect = `${location.pathname}${location.search}`;
    const signInUrl = needsUpgradeRedirect
      ? buildSignInUrl({ mode: "signup", redirect: upgradeRedirect })
      : "/signin";
    return <Navigate to={signInUrl} state={{ from: upgradeRedirect }} replace />;
  }
  return children;
}
